import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import MobileBottomNav from "@/components/MobileBottomNav";
import InstallPrompt from "@/components/InstallPrompt";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Sun, Moon, TrendingUp, List, LogIn, LogOut, Shield, CreditCard, UserCircle, Target, Lightbulb, FileText, Home, Menu, Wallet, Search } from "lucide-react";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (path: string) => { navigate(path); setMenuOpen(false); };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Disclaimer Banner */}
      <div className="bg-primary/5 text-center py-1.5 px-4 text-xs font-medium text-muted-foreground border-b border-border">
        ⚠️ Educational purpose only. Not financial advice.
      </div>

      {/* Header — Coinbase clean nav */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="container flex items-center justify-between h-14 px-4">
          <Link to="/" className="flex items-center gap-2.5 font-bold text-lg text-primary hover:opacity-80 transition-opacity">
            <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="tracking-tight">BeanZ</span>
          </Link>

          <nav className="flex items-center gap-2">
            {/* Desktop nav links — hidden on mobile */}
            {user && (
              <div className="hidden md:flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => navigate("/")} aria-label="Home" className="h-9 w-9 text-muted-foreground hover:text-foreground">
                  <Home className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/search")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Search className="h-4 w-4" /> Search
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/watchlist")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <List className="h-4 w-4" /> Watchlist
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/plans")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <CreditCard className="h-4 w-4" /> Plans
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/billing")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <FileText className="h-4 w-4" /> Billing
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/wallet")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Target className="h-4 w-4" /> Growth Plan
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/trading-wallet")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Wallet className="h-4 w-4" /> Wallet
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/daily-tips")} className="gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Lightbulb className="h-4 w-4" /> Tips
                </Button>
              </div>
            )}

            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} className="h-9 w-9 text-muted-foreground hover:text-foreground">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {user ? (
              <>
                {isAdmin && (
                  <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} aria-label="Admin" className="h-9 w-9 text-muted-foreground hover:text-foreground hidden md:inline-flex">
                    <Shield className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => navigate("/profile")} aria-label="Profile" className="h-9 w-9 text-muted-foreground hover:text-foreground hidden md:inline-flex">
                  <UserCircle className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={signOut} className="gap-2 text-sm rounded-full px-4 hidden md:inline-flex">
                  <LogOut className="h-4 w-4" /> Sign out
                </Button>

                {/* Mobile hamburger */}
                <Button variant="ghost" size="icon" onClick={() => setMenuOpen(true)} aria-label="Open menu" className="h-9 w-9 text-muted-foreground hover:text-foreground md:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => navigate("/login")} className="gap-2 text-sm rounded-full px-5">
                <LogIn className="h-4 w-4" /> <span className="hidden sm:inline">Sign in</span>
              </Button>
            )}
          </nav>

          {/* Mobile slide-out menu */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetContent side="right" className="w-64">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-1 mt-4">
                <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/search")}><Search className="h-4 w-4" /> Stock Search</Button>
                <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/watchlist")}><List className="h-4 w-4" /> Watchlist</Button>
                <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/plans")}><CreditCard className="h-4 w-4" /> Plans</Button>
                <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/billing")}><FileText className="h-4 w-4" /> Billing</Button>
                <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/wallet")}><Target className="h-4 w-4" /> Growth Plan</Button>
                <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/trading-wallet")}><Wallet className="h-4 w-4" /> Wallet</Button>
                {isAdmin && (
                  <Button variant="ghost" className="justify-start gap-3" onClick={() => go("/admin")}><Shield className="h-4 w-4" /> Admin</Button>
                )}
                <div className="border-t border-border my-2" />
                <Button variant="ghost" className="justify-start gap-3 text-destructive" onClick={() => { signOut(); setMenuOpen(false); }}><LogOut className="h-4 w-4" /> Sign out</Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Breadcrumb */}
      <Breadcrumb />

      {/* Main */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-4 pb-20 md:pb-4 text-center text-xs text-muted-foreground space-y-1">
        <div>
          <Link to="/faq" className="hover:text-foreground transition-colors underline">FAQ</Link>
        </div>
        <div>BeanZ Trade © {new Date().getFullYear()} — Educational use only</div>
      </footer>

      {/* Mobile bottom nav */}
      <MobileBottomNav />
      <InstallPrompt />
    </div>
  );
};

export default Layout;
